import type { TFunction } from "i18next"

import type { SpanHeatmapPlotView } from "types/dashboard.types"
import type { SpanDatum } from "types/spans.types"

import {
  LIMITING_INTENSITY_THRESHOLD,
  getIntensity,
} from "helpers/utils/limitingSpanColor"
import type { WindowDragOrigin } from "helpers/utils/spanRibbonWindow"

export interface SpanPoint {
  spanIndex: number
  spanId: number | null
  pct: number
  count: number
}

export interface HeatmapRow {
  // "spans" is the neutral lane, which stands in for both methods at once.
  dataKey: "aar" | "dlr" | "spans"
  labelKey: string
  totalCount: number
  points: SpanPoint[]
}

export interface SpanInfo {
  spanIndex: number
  spanId: number | null
  values: {
    dataKey: HeatmapRow["dataKey"]
    labelKey: string
    pct: number
    count: number
    intensity: number
  }[]
}

// One lane per method, reusing the series labels so the map and ribbon match.
// No spanData means lanes with no points, which the ribbon reads as no data.
export function getLanes(view: SpanHeatmapPlotView, spanData: SpanDatum[]): HeatmapRow[] {
  return view.properties.series.map((s) => {
    const points: SpanPoint[] = spanData.map((d) => ({
      spanIndex: d.spanIndex,
      spanId: d.spanId,
      pct: d[s.dataKey].pct,
      count: d[s.dataKey].count,
    }))
    return {
      dataKey: s.dataKey,
      labelKey: s.labelKey,
      totalCount: points.reduce((sum, p) => sum + p.count, 0),
      points,
    }
  })
}

// Zero counts are safe: neutral mode prints no figures, so nothing here reads
// as a measurement.
export function getNeutralLane(spanData: SpanDatum[]): HeatmapRow[] {
  return [
    {
      dataKey: "spans",
      labelKey: "spans.ampacity.lane.spans",
      totalCount: 0,
      points: spanData.map((d) => ({
        spanIndex: d.spanIndex,
        spanId: d.spanId,
        pct: 0,
        count: 0,
      })),
    },
  ]
}

// The AAR/DLR label gutter. Every pixel here is one the lanes don't get.
export const LABEL_WIDTH = 32
export const ROW_HEIGHT = 24
export const ROW_GAP = 5
export const TICK_HEIGHT = 4
export const X_AXIS_HEIGHT = 16
// Gap between bars, carried as padding inside each cell so it can't accumulate.
export const CELL_GAP = 2
// Holds that method's arrows.
export const MARKER_BAND_HEIGHT = 9
export const ARROW_WIDTH = 8
export const ARROW_HEIGHT = 7
// Nudge for when one span is both peak and current, so the arrows don't overlap.
export const ARROW_SPLIT = ARROW_WIDTH / 2 + 1
// An 8px triangle is hard to point at, and the tooltip explains the arrows.
export const MARKER_HIT_WIDTH = 16

export const DEFAULT_WINDOW = 120

/**
 * Whether the line is long enough to need the scrubber. Keyed off the default
 * window and not the live size: "Fit all" must not remove the way back.
 */
export const needsScrubber = (total: number, windowSize?: number): boolean =>
  total > (windowSize ?? DEFAULT_WINDOW)

// The window can't be dragged narrower than this, or bars stop being hittable.
export const MIN_WINDOW = 20
export const SCRUBBER_TOP_GAP = 6
export const SCRUBBER_LABEL_HEIGHT = 26
export const SCRUBBER_HEIGHT = 24
export const HANDLE_WIDTH = 11
export const HANDLE_GRIP_WIDTH = 4
// Dims the stretch of line the lanes aren't showing.
export const SCRUBBER_MASK_COLOR = "rgba(13,16,22,0.62)"
// Lane title above the neutral strip, where the gutter would be.
export const LANE_TITLE_HEIGHT = 18
// Ramp bar, its top margin and the two captions under it.
const LEGEND_BLOCK_HEIGHT = 43
// Readout and event totals, their vertical margins included.
export const SUMMARY_ROW_HEIGHT = 33
export interface RibbonBlockOptions {
  lanes: number
  rowHeight: number
  showMarkerBands: boolean
  showLaneTitle: boolean
  showSummaryRow: boolean
  showLegend: boolean
  showScrubber: boolean
}

// The one place the ribbon's vertical budget is written down. The skeleton
// uses the same constants, so a change here can't leave the card scrolling.
export const getRibbonFixedHeight = ({
  lanes,
  rowHeight,
  showMarkerBands,
  showLaneTitle,
  showSummaryRow,
  showLegend,
  showScrubber,
}: RibbonBlockOptions): number =>
  (showSummaryRow ? SUMMARY_ROW_HEIGHT : 0) +
  (showLaneTitle ? LANE_TITLE_HEIGHT : 0) +
  lanes * ((showMarkerBands ? MARKER_BAND_HEIGHT : 0) + rowHeight + ROW_GAP) +
  TICK_HEIGHT +
  X_AXIS_HEIGHT +
  (showScrubber ? SCRUBBER_TOP_GAP + SCRUBBER_LABEL_HEIGHT + SCRUBBER_HEIGHT : 0) +
  (showLegend ? LEGEND_BLOCK_HEIGHT : 0)

export interface DragSession extends WindowDragOrigin {
  // Measured once on pointer-down, never re-read while the pointer moves.
  rect: DOMRect
  pointerId: number
  rafId: number | null
  pendingClientX: number | null
}

// Markers arrive as span ids, the grid works in indexes. Over every span, not
// just the window, so one outside it is absent rather than misplaced.
export const getSpanIndexesById = (rows: HeatmapRow[]): Map<number, number> => {
  const map = new Map<number, number>()
  for (const pt of rows[0]?.points ?? []) {
    if (pt.spanId != null) map.set(pt.spanId, pt.spanIndex)
  }
  return map
}

// Absolute (0-based) indexes of the spans the detail lanes are showing.
export const getWindowIndexes = (start: number, size: number): number[] =>
  Array.from({ length: size }, (_, i) => start + i)

// Both methods' figures for one span — what the readout prints.
export const getSpanInfo = (
  rows: HeatmapRow[],
  spanIndex: number | null,
  maxPct: number,
): SpanInfo | null => {
  if (spanIndex == null) return null
  const first = rows[0]?.points[spanIndex - 1]
  if (!first) return null
  return {
    spanIndex,
    spanId: first.spanId,
    values: rows.map((r) => {
      const pt = r.points[spanIndex - 1]
      const pct = pt?.pct ?? 0
      return {
        dataKey: r.dataKey,
        labelKey: r.labelKey,
        pct,
        count: pt?.count ?? 0,
        intensity: getIntensity(pct, maxPct),
      }
    }),
  }
}

// The whole line as one gradient, a stop per span centre, so neighbours blend
// into a heat strip. Also one node to repaint on a drag instead of 250.
export const getOverviewGradient = (
  rows: HeatmapRow[],
  maxPct: number,
  colorFor: (intensity: number) => string,
  quietColor: string,
): string => {
  const points = rows[0]?.points ?? []
  if (points.length === 0) return "none"
  const stops = points.map((_, index) => {
    // The worse of the two methods decides the colour, same as the map segment.
    const pct = Math.max(...rows.map((r) => r.points[index]?.pct ?? 0))
    const intensity = getIntensity(pct, maxPct)
    const color =
      intensity > LIMITING_INTENSITY_THRESHOLD ? colorFor(intensity) : quietColor
    const at = ((index + 0.5) / points.length) * 100
    return `${color} ${at.toFixed(3)}%`
  })
  return `linear-gradient(to right, ${stops.join(", ")})`
}

/** Methods whose last reported limiting span is this one. */
export const getCurrentLimiterMethods = (
  spanId: number | null,
  markersByKey?: Partial<Record<"aar" | "dlr", { currentSpanId?: number | null }>>,
): ("aar" | "dlr")[] =>
  spanId == null
    ? []
    : (["aar", "dlr"] as const).filter(
        (key) => markersByKey?.[key]?.currentSpanId === spanId,
      )

/** The note the ribbon and the summary card both print for those methods. */
export const getLastLimiterNote = (
  methods: ("aar" | "dlr")[],
  t: TFunction,
): string | null =>
  methods.length === 0
    ? null
    : String(
        t("spans.ampacity.markers.current", {
          method: methods
            .map((key) => key.toUpperCase())
            .join(` ${t("spans.ampacity.methodJoiner")} `),
        }),
      )

// The spans a lane flags: the most frequent one and the one limiting now.
export interface LaneMarkers {
  peakSpanId?: number | null
  currentSpanId?: number | null
  // ISO timestamp of when it was last reported as limiting.
  currentTime?: string | null
}

// Where a lane's two markers sit, and the frequency colouring the peak one.
export const getLaneMarkers = (
  row: HeatmapRow,
  indexBySpanId: Map<number, number>,
  markers?: LaneMarkers,
): {
  peakIndex: number | null
  currentIndex: number | null
  // One span can be both, and same-size arrows would sit on top of each other.
  isSplit: boolean
  peakPct: number
  currentTime: string | null
} => {
  const peakIndex =
    markers?.peakSpanId != null ? (indexBySpanId.get(markers.peakSpanId) ?? null) : null
  const currentIndex =
    markers?.currentSpanId != null
      ? (indexBySpanId.get(markers.currentSpanId) ?? null)
      : null
  return {
    peakIndex,
    currentIndex,
    isSplit: peakIndex != null && peakIndex === currentIndex,
    peakPct: peakIndex != null ? (row.points[peakIndex - 1]?.pct ?? 0) : 0,
    currentTime: markers?.currentTime ?? null,
  }
}
